import React, { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Plus, Loader2, Link as LinkIcon, Image, X, Video, PawPrint } from 'lucide-react';
import { toast } from 'sonner';
import { TagSelector } from '../TagBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'care-tips', label: 'Care Tips' },
  { value: 'identification', label: 'Breed ID' },
  { value: 'showcase', label: 'Showcase' },
  { value: 'question', label: 'Question' },
  { value: 'news', label: 'News' }
];

export function CreatePostDialog({ open, onOpenChange, onPostCreated }) {
  const [creating, setCreating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingPetPhoto, setUploadingPetPhoto] = useState(false);
  const [fetchingPreview, setFetchingPreview] = useState(false);
  const [showDiscardAlert, setShowDiscardAlert] = useState(false);
  const [myPets, setMyPets] = useState([]);
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const petPhotoInputRef = useRef(null);
  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    category: 'general',
    image_url: '',
    images: [],
    video_url: '',
    tags: [],
    link_preview: null,
    pet_avatar_url: ''
  });
  const MAX_IMAGES = 4;
  const MAX_VIDEO_MB = 25;
  const MAX_PET_PHOTO_MB = 5;

  // Load the current user's pets once the dialog opens so they can pick
  // a saved pet photo as the post avatar.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API}/pets/me`);
        if (cancelled) return;
        const petsWithPhotos = (response.data || []).filter(p => p.image_url);
        setMyPets(petsWithPhotos);
      } catch {
        // Silent — feature is optional
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Extract URLs from text
  const extractUrl = (text) => {
    const urlPattern = /https?:\/\/[^\s<>"{}|\\^`[\]]+/g;
    const matches = text.match(urlPattern);
    return matches ? matches[0] : null;
  };

  // Fetch link preview when URL is detected
  const fetchLinkPreview = useCallback(async (url) => {
    if (!url) return;
    setFetchingPreview(true);
    try {
      const response = await axios.get(`${API}/link-preview/fetch`, {
        params: { url }
      });
      setNewPost(prev => ({ ...prev, link_preview: response.data }));
    } catch (error) {
      console.error('Link preview fetch failed:', error.response?.data?.detail || error.message);
    } finally {
      setFetchingPreview(false);
    }
  }, []);

  // Handle content change and detect URLs
  const handleContentChange = (content) => {
    setNewPost(prev => ({ ...prev, content }));
    
    const url = extractUrl(content);
    if (url && !newPost.link_preview) {
      fetchLinkPreview(url);
    } else if (!url && newPost.link_preview) {
      setNewPost(prev => ({ ...prev, link_preview: null }));
    }
  };

  // Handle image upload — supports up to MAX_IMAGES total
  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = MAX_IMAGES - newPost.images.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_IMAGES} images per post`);
      return;
    }
    const toUpload = files.slice(0, remaining);
    if (files.length > remaining) {
      toast.warning(`Only adding ${remaining} more (max ${MAX_IMAGES} per post).`);
    }

    setUploading(true);
    try {
      const uploaded = [];
      for (const file of toUpload) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`Image too large (${fileSizeMB}MB). Maximum 10MB allowed.`);
          continue;
        }
        const formData = new FormData();
        formData.append('file', file);
        const response = await axios.post(`${API}/uploads/screenshot`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const imageUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
        uploaded.push(imageUrl);
      }
      if (uploaded.length) {
        setNewPost(prev => {
          const next = [...prev.images, ...uploaded].slice(0, MAX_IMAGES);
          return { ...prev, images: next, image_url: next[0] || '' };
        });
        toast.success(uploaded.length === 1 ? 'Image uploaded' : `${uploaded.length} images uploaded`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to upload image';
      toast.error(`${detail}`);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeImageAt = (idx) => {
    setNewPost(prev => {
      const next = prev.images.filter((_, i) => i !== idx);
      return { ...prev, images: next, image_url: next[0] || '' };
    });
  };

  // Video upload — single 25MB clip per post
  const handleVideoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_VIDEO_MB * 1024 * 1024) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(1);
      toast.error(`Video too large (${sizeMB}MB). Maximum ${MAX_VIDEO_MB}MB.`);
      if (videoInputRef.current) videoInputRef.current.value = '';
      return;
    }

    setUploadingVideo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/uploads/video`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const videoUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setNewPost(prev => ({ ...prev, video_url: videoUrl }));
      toast.success('Video uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload video');
    } finally {
      setUploadingVideo(false);
      if (videoInputRef.current) videoInputRef.current.value = '';
    }
  };

  const removeVideo = () => {
    setNewPost(prev => ({ ...prev, video_url: '' }));
  };

  // Pet photo picker — either pick from a saved pet or upload a fresh photo
  const handlePickSavedPet = (petId) => {
    if (petId === '__none__') {
      setNewPost(prev => ({ ...prev, pet_avatar_url: '' }));
      return;
    }
    const pet = myPets.find(p => p.id === petId);
    if (pet?.image_url) {
      setNewPost(prev => ({ ...prev, pet_avatar_url: pet.image_url }));
    }
  };

  const handlePetPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_PET_PHOTO_MB * 1024 * 1024) {
      toast.error(`Pet photo too large. Max ${MAX_PET_PHOTO_MB}MB.`);
      if (petPhotoInputRef.current) petPhotoInputRef.current.value = '';
      return;
    }
    setUploadingPetPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/uploads/pet-image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = `${process.env.REACT_APP_BACKEND_URL}${response.data.url}`;
      setNewPost(prev => ({ ...prev, pet_avatar_url: url }));
      toast.success('Pet photo attached');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload pet photo');
    } finally {
      setUploadingPetPhoto(false);
      if (petPhotoInputRef.current) petPhotoInputRef.current.value = '';
    }
  };

  const clearPetPhoto = () => {
    setNewPost(prev => ({ ...prev, pet_avatar_url: '' }));
  };

  const handleCreatePost = async () => {
    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast.error('Please fill in title and content');
      return;
    }
    setCreating(true);
    try {
      const response = await axios.post(`${API}/posts`, newPost);
      setNewPost({ title: '', content: '', category: 'general', image_url: '', images: [], video_url: '', tags: [], link_preview: null, pet_avatar_url: '' });
      onOpenChange(false);
      onPostCreated?.(response.data);
      toast.success('Post created!');
    } catch (error) {
      const status = error.response?.status;
      if (status === 429) {
        toast.error("You're posting fast! Please wait a moment and try again.");
      } else {
        toast.error('Failed to create post');
      }
    } finally {
      setCreating(false);
    }
  };

  const resetForm = () => {
    setNewPost({ title: '', content: '', category: 'general', image_url: '', images: [], video_url: '', tags: [], link_preview: null, pet_avatar_url: '' });
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (videoInputRef.current) videoInputRef.current.value = '';
    if (petPhotoInputRef.current) petPhotoInputRef.current.value = '';
  };

  const hasUnsavedContent = newPost.title.trim() || newPost.content.trim() || newPost.images.length > 0 || !!newPost.video_url || !!newPost.pet_avatar_url;

  const handleDialogClose = (isOpen) => {
    if (!isOpen && hasUnsavedContent) {
      setShowDiscardAlert(true);
      return;
    }
    if (!isOpen) resetForm();
    onOpenChange(isOpen);
  };

  const handleDiscard = () => {
    setShowDiscardAlert(false);
    resetForm();
    onOpenChange(false);
  };

  return (
    <>
    <Dialog open={open} onOpenChange={handleDialogClose}>
      <DialogTrigger asChild>
        <Button className="btn-primary" data-testid="create-post-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Post
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Create a Post</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <Input
            placeholder="Title"
            value={newPost.title}
            onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
            data-testid="post-title-input"
            className="input-field"
          />
          <div className="relative">
            <Textarea
              placeholder="Share your thoughts, tips, or questions... Paste a link to auto-generate a preview!"
              value={newPost.content}
              onChange={(e) => handleContentChange(e.target.value)}
              rows={4}
              data-testid="post-content-input"
              className="resize-none"
            />
            {fetchingPreview && (
              <div className="absolute right-2 top-2">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Link Preview */}
          {newPost.link_preview && (
            <div className="relative">
              <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                <LinkIcon className="w-3 h-3" />
                Link preview detected
              </div>
              <div className="border rounded-lg p-3 bg-muted/50">
                <div className="flex gap-3">
                  {newPost.link_preview.image && (
                    <img 
                      src={newPost.link_preview.image} 
                      alt="" 
                      className="w-20 h-20 object-cover rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm line-clamp-1">{newPost.link_preview.title}</p>
                    <p className="text-xs text-muted-foreground line-clamp-2">{newPost.link_preview.description}</p>
                    <p className="text-xs text-primary mt-1">{newPost.link_preview.domain}</p>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setNewPost(prev => ({ ...prev, link_preview: null }))}
                className="absolute top-0 right-0 text-xs text-muted-foreground hover:text-foreground"
              >
                Remove
              </button>
            </div>
          )}

          <div className="flex gap-4 items-start">
            <Select
              value={newPost.category}
              onValueChange={(val) => setNewPost({ ...newPost, category: val })}
            >
              <SelectTrigger className="w-40" data-testid="post-category-select">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Image Upload — supports up to 4 images */}
          <div>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageSelect}
              ref={fileInputRef}
              className="hidden"
              id="post-image-upload"
            />
            {newPost.images.length > 0 ? (
              <div className="space-y-2" data-testid="post-images-preview">
                <div className={`grid gap-2 ${newPost.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  {newPost.images.map((url, idx) => (
                    <div key={url} className="relative group">
                      <img
                        src={url}
                        alt={`Upload ${idx + 1}`}
                        className={`w-full ${newPost.images.length === 1 ? 'h-48' : 'h-32'} object-cover rounded-lg`}
                      />
                      <button
                        type="button"
                        onClick={() => removeImageAt(idx)}
                        className="absolute top-1 right-1 p-1 bg-black/50 rounded-full hover:bg-black/70"
                        disabled={uploading}
                        data-testid={`remove-image-${idx}`}
                        aria-label="Remove image"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">{newPost.images.length} of {MAX_IMAGES} images</span>
                  {newPost.images.length < MAX_IMAGES && (
                    <label
                      htmlFor="post-image-upload"
                      className="text-xs text-primary hover:underline cursor-pointer"
                      data-testid="add-more-images"
                    >
                      + Add more
                    </label>
                  )}
                </div>
                {uploading && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="w-3 h-3 animate-spin" /> Uploading…
                  </div>
                )}
              </div>
            ) : (
              <label
                htmlFor="post-image-upload"
                className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                title={`Up to ${MAX_IMAGES} images. Each auto-compressed. 10MB max each.`}
                data-testid="post-image-dropzone"
              >
                <Image className="w-5 h-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Add images</span>
                <span className="text-xs text-muted-foreground/70">(up to {MAX_IMAGES}, auto-compressed)</span>
              </label>
            )}
          </div>

          {/* Video Upload — single short clip per post (≤25MB) */}
          <div>
            <input
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              onChange={handleVideoSelect}
              ref={videoInputRef}
              className="hidden"
              id="post-video-upload"
            />
            {newPost.video_url ? (
              <div className="space-y-2" data-testid="post-video-preview">
                <div className="relative">
                  <video
                    src={newPost.video_url}
                    controls
                    className="w-full max-h-72 rounded-lg bg-black"
                    preload="metadata"
                  />
                  <button
                    type="button"
                    onClick={removeVideo}
                    className="absolute top-1 right-1 p-1 bg-black/60 rounded-full hover:bg-black/80"
                    data-testid="remove-video-btn"
                    aria-label="Remove video"
                  >
                    <X className="w-3.5 h-3.5 text-white" />
                  </button>
                </div>
                <span className="text-xs text-muted-foreground">Video attached</span>
              </div>
            ) : (
              <label
                htmlFor="post-video-upload"
                className="flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-lg cursor-pointer hover:bg-muted/50 transition-colors"
                title={`MP4, WebM, or MOV. Up to ${MAX_VIDEO_MB}MB.`}
                data-testid="post-video-dropzone"
              >
                {uploadingVideo ? (
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                ) : (
                  <Video className="w-5 h-5 text-muted-foreground" />
                )}
                <span className="text-sm text-muted-foreground">
                  {uploadingVideo ? 'Uploading…' : 'Add a short video'}
                </span>
                <span className="text-xs text-muted-foreground/70">(MP4/WebM/MOV, ≤{MAX_VIDEO_MB}MB)</span>
              </label>
            )}
          </div>

          {/* Pet Photo Picker — shown as the avatar next to this post */}
          <div className="border rounded-lg p-3 bg-amber-50/40" data-testid="post-pet-avatar-section">
            <div className="flex items-center gap-2 mb-2">
              <PawPrint className="w-4 h-4 text-amber-700" />
              <span className="text-sm font-medium">Pet photo (optional)</span>
              <span className="text-xs text-muted-foreground">— shows next to your post instead of a letter</span>
            </div>
            {newPost.pet_avatar_url ? (
              <div className="flex items-center gap-3">
                <img
                  src={newPost.pet_avatar_url}
                  alt="Selected pet"
                  className="w-12 h-12 rounded-full object-cover ring-2 ring-amber-200"
                  data-testid="post-pet-avatar-preview"
                />
                <div className="flex-1 text-xs text-muted-foreground">Pet photo attached</div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearPetPhoto}
                  data-testid="post-pet-avatar-clear"
                >
                  <X className="w-3 h-3 mr-1" /> Remove
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center gap-2">
                {myPets.length > 0 && (
                  <Select onValueChange={handlePickSavedPet}>
                    <SelectTrigger className="w-52" data-testid="post-pet-avatar-select">
                      <SelectValue placeholder="Use a saved pet…" />
                    </SelectTrigger>
                    <SelectContent>
                      {myPets.map(pet => (
                        <SelectItem key={pet.id} value={pet.id}>{pet.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePetPhotoUpload}
                  ref={petPhotoInputRef}
                  className="hidden"
                  id="post-pet-photo-upload"
                />
                <label
                  htmlFor="post-pet-photo-upload"
                  className="inline-flex items-center gap-2 px-3 py-1.5 border rounded-md text-sm cursor-pointer hover:bg-muted transition-colors"
                  data-testid="post-pet-avatar-upload"
                >
                  {uploadingPetPhoto ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Image className="w-4 h-4" />
                  )}
                  Upload pet photo
                </label>
                <span className="text-[11px] text-muted-foreground">≤{MAX_PET_PHOTO_MB}MB</span>
              </div>
            )}
          </div>

          {/* Tag Selector */}
          <TagSelector
            selectedTags={newPost.tags}
            onChange={(tags) => setNewPost({ ...newPost, tags })}
          />

          <Button 
            className="w-full btn-primary" 
            onClick={handleCreatePost}
            disabled={creating}
            data-testid="submit-post-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Publish Post
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    <AlertDialog open={showDiscardAlert} onOpenChange={setShowDiscardAlert}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Discard post?</AlertDialogTitle>
          <AlertDialogDescription>
            You have unsaved changes. Are you sure you want to discard this post?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="continue-editing-btn">Continue Editing</AlertDialogCancel>
          <AlertDialogAction onClick={handleDiscard} className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="discard-post-btn">
            Discard
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

export default CreatePostDialog;
